import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/recommendation-reason-accordion';
import type { Carecommendation } from 'lumen/models';
import type { OccupancyRecommendationReason } from 'lumen/models/pa-recommendation-reason';
import {
  RECOMMENDATION_REASON_TRANSLATION_KEY,
  RECOMMENDATION_REASON_TYPE_OCCUPANCY,
} from 'lumen/utils/constants/pricing-assistant-constants';

interface FormattedOccupancyReason {
  details: string[];
  icon: IconName;
  isIncrease: boolean | null;
  label: string;
}

interface HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasonsSignature {
  Args: {
    carecommendationOfSelectedDay?: Carecommendation;
  };
}

export default class HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasons extends Component<HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasonsSignature> {
  @service declare intl: Services['intl'];

  get reasons(): FormattedOccupancyReason[] {
    const { carecommendationOfSelectedDay } = this.args;

    const reasons =
      carecommendationOfSelectedDay?.paRecommendationReason?.occupancyReasons;

    if (!reasons) {
      return [];
    }

    return reasons.reduce((accumulator, reason) => {
      /*
        Only show reasons for which we have a translation on frontend.
        Currently, recommendations without reasons return a fallback
        translation key, but they are not defined in FE yet.
      */
      if (RECOMMENDATION_REASON_TRANSLATION_KEY[reason.translation_key]) {
        const details = this.getDetails(reason);
        const icon =
          reason.reason_type ===
          RECOMMENDATION_REASON_TYPE_OCCUPANCY.VS_YESTERDAY
            ? 'user-add'
            : 'meals-room-only';
        const label = this.getLabel(reason);

        accumulator.push({
          details,
          icon,
          isIncrease: reason.is_increase,
          label,
        });
      }

      return accumulator;
    }, [] as FormattedOccupancyReason[]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private getDetails(reason: OccupancyRecommendationReason) {
    return reason.details.map((detail) => {
      const {
        actual,
        occupancy,
        reason_type,
        room_amount,
        translation_key,
        yesterday,
      } = detail;

      const translationKey =
        RECOMMENDATION_REASON_TRANSLATION_KEY[translation_key];

      if (!translationKey) {
        return '';
      }

      switch (reason_type) {
        case RECOMMENDATION_REASON_TYPE_OCCUPANCY.PREDICTED: {
          return this.intl.t(translationKey, {
            occupancy: this.toPercentage(occupancy),
          });
        }

        case RECOMMENDATION_REASON_TYPE_OCCUPANCY.VS_EXPECTED: {
          return this.intl.t(translationKey, {
            actual: this.toPercentage(actual),
          });
        }

        case RECOMMENDATION_REASON_TYPE_OCCUPANCY.VS_YESTERDAY: {
          return this.intl.t(translationKey, {
            actual: this.toPercentage(actual),
            difference: this.toPercentage(actual - yesterday, true),
            roomAmount: room_amount,
          });
        }

        default: {
          return '';
        }
      }
    });
  }

  private getLabel(reason: OccupancyRecommendationReason): string {
    const { translation_key } = reason;

    return this.intl.t(RECOMMENDATION_REASON_TRANSLATION_KEY[translation_key]);
  }

  toPercentage(ratio: number, showSign?: boolean): string {
    if (showSign) {
      return this.intl.formatNumber(ratio, {
        signDisplay: 'always',
        style: 'percent',
      });
    }

    return this.intl.formatNumber(ratio, { style: 'percent' });
  }

  <template>
    {{#each this.reasons as |reason|}}
      <HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion
        @reason={{reason}}
      >
        {{#each reason.details as |detail|}}
          <p class="text-200--regular">{{detail}}</p>
        {{/each}}
      </HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion>
    {{/each}}
  </template>
}
